/**
 * Verbes métier des photos de chantier.
 *
 * Tout est en N2 : déposer une photo, en retirer une, marquer l'archive
 * téléchargée. Aucun de ces gestes ne sort de l'entreprise, et aucun n'est
 * irréversible — le retrait est une suppression douce, rattrapable tant que la
 * purge n'est pas passée. C'est ce qui les distingue d'une vraie suppression,
 * qui serait N3.
 *
 * La purge définitive, elle, n'est pas un verbe : personne ne la déclenche.
 * Elle appartient à la tâche planifiée `purge-project-photos`, qui l'applique
 * après trois avertissements et en garde la trace.
 */
import { ActionLevel } from "../../levels.js";
import { DomainError } from "../../errors.js";
import { defineService } from "../../service.js";
import type { PhotoRepository } from "./repository.js";
import { sortPhotos } from "./types.js";
import type { ProjectPhoto } from "./types.js";
import type {
  ListProjectPhotosInput,
  MarkPhotosDownloadedInput,
  RecordProjectPhotoInput,
  RemoveProjectPhotoInput,
  SharePhotoInput,
} from "./schema.js";

export function createPhotoServices(repo: PhotoRepository) {
  const listerPhotosChantier = defineService<ListProjectPhotosInput, ProjectPhoto[]>({
    name: "lister_photos_chantier",
    level: ActionLevel.Read,
    handler: async (ctx, input) =>
      sortPhotos(await repo.listByProject(ctx, input.projectId)),
  });

  const enregistrerPhotoChantier = defineService<RecordProjectPhotoInput, ProjectPhoto>({
    name: "enregistrer_photo_chantier",
    level: ActionLevel.ReversibleWrite,
    handler: (ctx, input) => {
      // Le premier segment du chemin est le tenant : c'est la convention sur
      // laquelle reposent les policies Storage (migration 20260201000900). Une
      // ligne pointant ailleurs désignerait un objet que personne ne pourra
      // jamais relire — la RLS du Storage le refuserait à tout le monde.
      const folder = input.storagePath.split("/")[0];
      if (folder !== ctx.tenantId) {
        throw new DomainError(
          "invalid_input",
          "Cette photo n'appartient pas à votre espace.",
          { storagePath: input.storagePath },
        );
      }

      return repo.record(ctx, {
        projectId: input.projectId,
        storagePath: input.storagePath,
      });
    },
  });

  const retirerPhotoChantier = defineService<RemoveProjectPhotoInput, { removed: true }>({
    name: "retirer_photo_chantier",
    level: ActionLevel.ReversibleWrite,
    handler: async (ctx, input) => {
      await repo.softDelete(ctx, input.photoId);
      return { removed: true };
    },
  });

  /**
   * Montrer cette photo au client, ou la retirer de son espace.
   *
   * Réservé au bureau : le terrain prend les photos, il ne décide pas ce que
   * le client voit. La base l'impose aussi — `project_photos` reste fermée en
   * écriture au terrain au-delà de l'ajout.
   *
   * `ReversibleWrite` : rien ne part à l'extérieur, et le geste se défait. Ce
   * qui sort de l'entreprise, c'est le lien — et c'est un autre verbe.
   */
  const partagerPhotoChantier = defineService<SharePhotoInput, ProjectPhoto>({
    name: "partager_photo_chantier",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron", "conducteur", "assistant"],
    handler: (ctx, input) => repo.setSharedWithClient(ctx, input.photoId, input.shared),
  });

  /**
   * Horodate le téléchargement de l'archive.
   *
   * Ce n'est pas une commodité d'affichage. C'est la moitié utile de la trace :
   * `photo_retention_notices` prouve que le patron a été prévenu, ce marqueur
   * dit s'il a récupéré ses photos ou non.
   */
  const marquerPhotosArchivees = defineService<
    MarkPhotosDownloadedInput,
    { marked: number }
  >({
    name: "marquer_photos_archivees",
    level: ActionLevel.ReversibleWrite,
    handler: async (ctx, input) => ({
      marked: await repo.markDownloaded(ctx, input.photoIds),
    }),
  });

  return {
    listerPhotosChantier,
    enregistrerPhotoChantier,
    retirerPhotoChantier,
    partagerPhotoChantier,
    marquerPhotosArchivees,
  };
}

export type PhotoServices = ReturnType<typeof createPhotoServices>;
